import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { MessageSquare, Mic } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { InterviewsList } from '~/components/features/admin/interviews-list';
import { PageContainer } from '~/components/layouts/page-container';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { api } from '~/lib/api';
import { cn } from '~/lib/utils';

type ChannelFilter = 'all' | 'voice' | 'text';

const interviewsQueryOptions = (page: number, orgId: string | undefined, channel: ChannelFilter) =>
  queryOptions({
    queryKey: ['admin', 'interviews', page, orgId, channel] as const,
    queryFn: async () => {
      const query: Record<string, string> = { page: String(page) };
      if (orgId !== undefined) query['orgId'] = orgId;
      if (channel !== 'all') query['channel'] = channel;
      const res = await api.api.admin.interviews.$get({ query });
      if (!res.ok) throw new Error('Failed to fetch interviews');
      return res.json();
    }
  });

const interviewStatsQueryOptions = queryOptions({
  queryKey: ['admin', 'interviews', 'stats'] as const,
  queryFn: async () => {
    const res = await api.api.admin.interviews.stats.$get();
    if (!res.ok) throw new Error('Failed to fetch interview stats');
    return res.json();
  }
});

const orgsQueryOptions = queryOptions({
  queryKey: ['admin', 'organizations'] as const,
  queryFn: async () => {
    const res = await api.api.admin.organizations.$get();
    if (!res.ok) throw new Error('Failed to fetch organizations');
    return res.json();
  }
});

export const Route = createFileRoute('/_authenticated/admin/interviews/')({
  validateSearch: z.object({
    page: z.number().int().min(1).default(1),
    orgId: z.string().optional(),
    channel: z.enum(['all', 'voice', 'text']).default('all')
  }),
  loaderDeps: ({ search }) => ({
    page: search.page,
    orgId: search.orgId,
    channel: search.channel
  }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        interviewsQueryOptions(deps.page, deps.orgId, deps.channel)
      ),
      context.queryClient.ensureQueryData(interviewStatsQueryOptions),
      context.queryClient.ensureQueryData(orgsQueryOptions)
    ]),
  component: InterviewsPage
});

function FilterGroup<T extends string>({
  value,
  options,
  onChange
}: {
  value: T;
  options: { value: T; label: string; icon?: React.ReactNode }[];
  onChange: (v: T) => void;
}): React.ReactNode {
  return (
    <div className="flex gap-1 rounded-lg border border-border p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => {
            onChange(opt.value);
          }}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function KpiCard({
  label,
  value,
  description
}: {
  label: string;
  value: string;
  description?: string;
}): React.ReactNode {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tabular-nums leading-tight">{value}</span>
      {description !== undefined && (
        <span className="text-xs text-muted-foreground">{description}</span>
      )}
    </div>
  );
}

function InterviewsPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('admin.interviews.title'));

  const { page, orgId, channel } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data } = useQuery(interviewsQueryOptions(page, orgId, channel));
  const { data: stats } = useQuery(interviewStatsQueryOptions);
  const { data: orgsData } = useQuery(orgsQueryOptions);

  const sessions = data?.sessions ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 100;
  const organizations = orgsData?.organizations ?? [];

  function handlePageChange(newPage: number): void {
    void navigate({ search: (prev) => ({ ...prev, page: newPage }) });
  }

  function handleOrgFilter(value: string | null): void {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        orgId: value === null || value === 'all' ? undefined : value
      })
    });
  }

  function handleChannelFilter(value: ChannelFilter): void {
    void navigate({ search: (prev) => ({ ...prev, page: 1, channel: value }) });
  }

  function formatRatio(value: number | null | undefined): string {
    if (value === null || value === undefined) return t('admin.interviews.kpi.noData');
    return `${String(Math.round(value * 100))}%`;
  }

  return (
    <PageContainer
      title={t('admin.interviews.title')}
      breadcrumbs={[{ label: t('admin.interviews.title') }]}
    >
      {stats !== undefined && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiCard
            label={t('admin.interviews.kpi.failedLast24h')}
            value={String(stats.failedLast24h)}
          />
          <KpiCard
            label={t('admin.interviews.kpi.restartedLast24h')}
            value={String(stats.restartedLast24h)}
          />
          <KpiCard
            label={t('admin.interviews.kpi.completedRatio')}
            value={formatRatio(stats.completedRatioLast24h)}
          />
          <KpiCard
            label={t('admin.interviews.kpi.passRatio')}
            value={formatRatio(stats.passedRatioLast24h)}
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={orgId ?? 'all'} onValueChange={handleOrgFilter}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder={t('admin.interviews.filter.allOrgs')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.interviews.filter.allOrgs')}</SelectItem>
            {organizations.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FilterGroup<ChannelFilter>
          value={channel}
          onChange={handleChannelFilter}
          options={[
            { value: 'all', label: t('admin.interviews.filter.allChannels') },
            {
              value: 'voice',
              label: t('admin.interviews.filter.voice'),
              icon: <Mic className="size-3.5" />
            },
            {
              value: 'text',
              label: t('admin.interviews.filter.chat'),
              icon: <MessageSquare className="size-3.5" />
            }
          ]}
        />
      </div>

      <InterviewsList
        sessions={sessions}
        total={total}
        page={page}
        limit={limit}
        onPageChange={handlePageChange}
      />
    </PageContainer>
  );
}
